import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type SubmitEventHandler } from 'react';
import { useTranslation } from 'react-i18next';

import { authMetadataQuery, loginMutation, type LoginResult } from '@rootsight/shared';
import { Button, ErrorNotice, Stack, TextField } from '@rootsight/ui';

import { api } from '../../api';

/**
 * PasswordStep is the email and password form, and the identity providers'
 * buttons below it. What the password earns -- a session, or a challenge for a
 * second factor -- is the caller's to act on.
 */
export function PasswordStep({
  onResult,
  onSso,
}: {
  onResult: (result: LoginResult) => void;
  /** Switches to single sign-on, which asks for an email instead. */
  onSso: () => void;
}) {
  const { t } = useTranslation(['auth', 'common']);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // The provider buttons are an extra: if the metadata cannot be loaded the
  // password form must still work, so its error is deliberately not shown.
  const metadata = useQuery(authMetadataQuery(api));
  const providers = metadata.data?.identityProviders ?? [];
  const saml = metadata.data?.samlEnabled === true;

  const login = useMutation({ ...loginMutation(api), onSuccess: onResult });

  const submit: SubmitEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    login.mutate({ email, password });
  };

  return (
    <Stack gap={6}>
      <form onSubmit={submit} noValidate>
        <Stack gap={4}>
          {login.isError && <ErrorNotice error={login.error} />}

          <TextField
            label={t('login.email')}
            type="email"
            name="email"
            value={email}
            onChange={setEmail}
            placeholder={t('login.emailPlaceholder')}
            autoComplete="username"
            isRequired
          />

          <TextField
            label={t('login.password')}
            type="password"
            name="password"
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            isRequired
          />

          <Button type="submit" variant="primary" size="lg" isPending={login.isPending}>
            {login.isPending ? t('login.submitting') : t('login.submit')}
          </Button>
        </Stack>
      </form>

      {(saml || providers.length > 0) && (
        <Stack align="center">
          <span>{t('login.sso.divider')}</span>
          {saml && (
            <Button size="lg" onPress={onSso}>
              {t('login.sso.useSso')}
            </Button>
          )}
          {providers.map((provider) => (
            <Button
              key={provider.id}
              size="lg"
              onPress={() => {
                window.location.assign(provider.loginUrl);
              }}
            >
              {t('login.sso.continue', { provider: provider.displayName })}
            </Button>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
